import { FeatherChevronRight, FeatherFlame, FeatherTags } from "@subframe/core";
import { createFileRoute, Link, Navigate } from "@tanstack/react-router";

import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";

export const Route = createFileRoute("/_authenticated/platform-settings/")({
  component: PlatformSettingsPage,
});

interface SettingLink {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

const SETTINGS: SettingLink[] = [
  {
    to: "/platform-settings/categories",
    icon: <FeatherTags className="h-5 w-5 text-primary-navy-800" />,
    title: "Categories",
    description: "Manage the platform-wide categories.",
  },
  {
    to: "/platform-settings/streak-rules",
    icon: <FeatherFlame className="h-5 w-5 text-primary-navy-800" />,
    title: "Streak rules",
    description:
      "Set how long after its due time a checklist still counts toward the streak.",
  },
];

function PlatformSettingsPage() {
  const { userRoleType, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }
  if (userRoleType !== "PLATFORM_ADMIN") {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div className="flex w-full flex-col items-start gap-8 bg-white animate-fade-in">
      {/* Header */}
      <div className="flex w-full flex-col items-start gap-1">
        <span className="text-metrics font-metrics text-primary-navy-800">
          Platform settings
        </span>
        <span className="text-body font-body text-subtext-color">
          Manage platform-wide configuration shared across all nursery groups.
        </span>
      </div>

      {/* Settings list */}
      <div className="flex w-full flex-col items-start gap-3">
        {SETTINGS.map((setting) => (
          <Link key={setting.to} to={setting.to} className="w-full">
            <div className="flex w-full items-center gap-4 rounded-md border border-solid border-secondary-beige-200 bg-default-background px-5 py-4 transition-colors hover:border-neutral-400 hover:bg-neutral-50">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-navy-50">
                {setting.icon}
              </div>
              <div className="flex grow shrink-0 basis-0 flex-col items-start">
                <span className="text-body-bold font-body-bold text-default-font">
                  {setting.title}
                </span>
                <span className="text-body font-body text-subtext-color">
                  {setting.description}
                </span>
              </div>
              <FeatherChevronRight className="h-5 w-5 shrink-0 text-subtext-color" />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
